import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Crown, ExternalLink } from "lucide-react";
import FirmIcon from "./FirmIcon";
import StarRating from "./StarRating";
import DiscountBadge from "./DiscountBadge";

export default function FirmCard({ firm, index = 0, showCrown = false }) {
  return (
    <motion.article
      data-testid={`firm-card-${firm.slug}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="card-violet group flex h-full flex-col p-6"
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3.5">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-line bg-elevate">
            <FirmIcon name={firm.icon} logo={firm.logo} />
          </div>
          <div>
            <h3 className="font-display text-lg font-700 tracking-tight text-white">{firm.name}</h3>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-muted">{firm.category} · EST. {firm.founded}</p>
          </div>
        </div>
        {showCrown && (
          <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 font-mono text-[9px] font-600 uppercase tracking-[0.1em] ${firm.pinned ? "border-gold/50 bg-gold/10 text-gold" : "border-signal/40 bg-signal/10 text-signal"}`} data-testid={`crown-${firm.slug}`}>
            <Crown size={11} /> {firm.pinned ? "Highest Rated" : "#1 Rated"}
          </span>
        )}
      </div>
      <div className="mt-4 flex items-center gap-2.5">
        <StarRating value={firm.rating} idPrefix={`card-${firm.slug}`} size={15} />
        <span className="font-mono text-sm font-700 text-white">{firm.rating.toFixed(1)}</span>
        {firm.review_count > 0 && <span className="font-mono text-[10px] text-muted">({firm.review_count} reviews)</span>}
      </div>
      <p className="mt-3 text-sm leading-relaxed text-muted line-clamp-2">{firm.tagline}</p>
      <div className="mt-5 grid grid-cols-3 gap-2 rounded-xl border border-line bg-ink/60 p-3.5">
        <div>
          <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-muted">Entry</p>
          <p className="mt-1 font-mono text-[13px] font-600 text-white">{firm.min_price || "—"}</p>
        </div>
        <div>
          <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-muted">Max Capital</p>
          <p className="mt-1 font-mono text-[13px] font-600 text-white">{firm.max_capital || "—"}</p>
        </div>
        <div>
          <p className="font-mono text-[8px] uppercase tracking-[0.18em] text-muted">Split</p>
          <p className="mt-1 font-mono text-[13px] font-600 text-signal">{firm.profit_split || "—"}</p>
        </div>
      </div>
      {firm.discount_code && (
        <div className="mt-4">
          <DiscountBadge label={firm.discount_label} code={firm.discount_code} slug={firm.slug} compact />
        </div>
      )}
      <div className="mt-auto flex items-center justify-between gap-3 pt-5">
        <Link
          to={`/firms/${firm.slug}`}
          data-testid={`firm-review-link-${firm.slug}`}
          className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.15em] text-white transition-colors hover:text-signal"
        >
          Full Review <ArrowUpRight size={13} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </Link>
        <a
          href={firm.website_url}
          target="_blank"
          rel="noopener noreferrer sponsored"
          data-testid={`firm-visit-${firm.slug}`}
          className="btn-beam btn-gradient inline-flex items-center gap-1.5 px-4 py-2 font-mono text-[10px] font-700 uppercase tracking-[0.12em] text-white"
        >
          Get Offer <ExternalLink size={11} />
        </a>
      </div>
    </motion.article>
  );
}
